import { useState } from 'react';
import type { JSX } from 'react';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { FeedbackPalette } from '../../types.js';

interface EmojiRatingProps {
  palette: FeedbackPalette;
  value: number | null;
  onChange: (rating: number) => void;
}

const RATING_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const EMOJI_SET: { icon: string; label: string }[] = [
  { icon: '😠', label: 'Terrible' },
  { icon: '😕', label: 'Bad' },
  { icon: '😐', label: 'Okay' },
  { icon: '😊', label: 'Good' },
  { icon: '😍', label: 'Loved it' },
];

export function EmojiRating({ palette, value, onChange }: EmojiRatingProps): JSX.Element {
  // Hover paints a soft preview tier so the jump from idle to committed
  // selection isn't abrupt.
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div
      style={{ display: 'flex', gap: 6, justifyContent: 'space-between', padding: '4px 0' }}
      onMouseLeave={() => setHovered(null)}
    >
      {EMOJI_SET.map((entry, index) => {
        const ratingValue = index + 1;
        const selected = value === ratingValue;
        const isHovered = hovered === ratingValue && !selected;
        const borderColor = selected
          ? palette.primary
          : isHovered
            ? hexWithAlpha(palette.primary, 0.6)
            : 'transparent';
        const background = selected
          ? palette.primarySoft
          : isHovered
            ? hexWithAlpha(palette.primary, 0.06)
            : 'transparent';
        const labelColor = selected
          ? palette.primary
          : isHovered
            ? palette.text
            : palette.textMuted;
        return (
          <button
            key={entry.icon}
            type="button"
            onClick={() => onChange(ratingValue)}
            onMouseEnter={() => setHovered(ratingValue)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              alignItems: 'center',
              padding: '10px 4px',
              border: `1.5px solid ${borderColor}`,
              background,
              borderRadius: 12,
              cursor: 'pointer',
              // 2 px lift on hover; selected stays flat.
              transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
              transition: `border-color 220ms ${RATING_EASE}, background-color 220ms ${RATING_EASE}, transform 220ms ${RATING_EASE}`,
            }}
          >
            <span style={{ fontSize: 28, lineHeight: 1 }}>{entry.icon}</span>
            <span
              style={{
                fontSize: 12,
                color: labelColor,
                fontWeight: selected ? 600 : 500,
                transition: `color 220ms ${RATING_EASE}`,
              }}
            >
              {entry.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
